import React from 'react';
import {
  CircularProgress,
  Divider,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { parseAmountWithCurrency } from '../../utils/helpers';
import { LoanProposalDetailsProps } from './types';
import AlertDialog from '../AlertDialog';
import LoanInfoRow from '../LoanRequestConditions/LoanInfoRow';

export const productName = (pType: LoanProposalDetailsProps['productType']) => {
  switch (pType) {
    case 'consumer':
      return 'crédito';
    case 'microLoan':
      return 'avance';
    case 'mortgage':
      return 'crédito hipotecario';
    case 'downPayment':
      return 'pie';
    default:
      return '';
  }
};

export default function LoanProposalDetails(props:LoanProposalDetailsProps) {
  const {
    loanData, currency, title, subtitle, productType, loading, highlight, dataPw,
  } = props;
  const theme = useTheme();

  if (loading) {
    return (
      <div>
        <Typography
          marginBottom={3}
          variant="h6"
          gutterBottom
        >
          Un momento...
        </Typography>
        <br />
        <CircularProgress size={30} color="inherit" />
        <Typography> Estamos analizando tus datos...</Typography>
        <br />
        <Typography color="text.secondary">El analisis de tus datos podría tardar hasta un minuto. ¡Tendrás tu respuesta lo antes posible!</Typography>
      </div>
    );
  }

  const loanDataArray = [
    {
      id: 1,
      label: 'Monto aprobado',
      value: parseAmountWithCurrency(
        currency,
        loanData.amount,
        false,
        true,
      ),
    },
    {
      id: 2,
      label: 'Plazo',
      value: `${loanData.periods} mes${loanData.periods > 1 ? 'es' : ''}`,
    },
    {
      id: 3,
      label: 'Monto cuota aproximada',
      value: parseAmountWithCurrency(
        currency,
        loanData.installment,
      ),
    },
    {
      id: 4,
      label: 'Gastos del crédito',
      labelEndAdornment: (
        <AlertDialog
          button={(
            <InfoOutlinedIcon style={{
              fontSize: '18px', paddingLeft: 2, marginBottom: '-6px', color: theme.palette.primary.main, cursor: 'pointer',
            }}
            />
            )}
          buttonVariant="text"
          buttonMaxWidth="fit-content"
          dialogContent={(
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 1, padding: 2,
            }}
            >
              <Typography variant="body2" textAlign="center">
                Costos administrativos asociados al procesamiento de tu crédito
              </Typography>
              <Divider sx={{ marginBlock: 2 }} />
              <LoanInfoRow
                label="Impuestos"
                value={loanData.taxes}
                currency={currency}
              />
              <LoanInfoRow
                label="Costos de originación"
                value={loanData.originCost}
                currency={currency}
              />
              {loanData.insuranceCost > 0 && (
                <LoanInfoRow
                  label="Seguros asociados"
                  value={loanData.insuranceCost}
                  currency={currency}
                />
              )}
            </Box>
          )}
          dialogTitle={(
            <Typography variant="h6" fontWeight="bold" textAlign="center">
              Gastos del crédito
            </Typography>
            )}
          hideSubmitButton
          centerButtons
          hideLogo
          maxWidth="xs"
        />
      ),
      value: parseAmountWithCurrency(
        currency,
        loanData.originCost + loanData.taxes + loanData.insuranceCost,
      ),
    },
    {
      id: 5,
      label: 'CAE',
      value: loanData.CAE.toLocaleString(
        'es-CL',
        { style: 'percent', maximumFractionDigits: 2 },
      ),
    },
    {
      id: 6,
      label: 'Interés mensual',
      value: (loanData.interestRate / 12).toLocaleString(
        'es-CL',
        { style: 'percent', maximumFractionDigits: 2 },
      ),
    },
  ];

  return (
    <>
      {typeof title === 'string' ? (
        <Typography
          marginBottom={3}
          fontWeight="bold"
          variant="h6"
          gutterBottom
          data-pw={dataPw}
          component="div"
        >
          {title}

        </Typography>
      ) : title}
      {typeof subtitle === 'string' ? (
        <Typography
          marginBottom={3}
          variant="body1"
          gutterBottom
        >
          {subtitle}

        </Typography>
      ) : subtitle}
      <>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          margin: '5px auto',
          padding: 10,
          backgroundColor: theme.palette.background.defaultSecondary,
          borderRadius: '15px',
          border: highlight ? `1px solid ${theme.palette.secondary.main}` : 'none',
          transition: '1s',
        }}
        >
          {loanDataArray.map((item) => (
            <div key={item.id}>
              <Typography color="text.primary" display="inline-flex" component="div" alignItems="center">
                {item.label}
                {item.labelEndAdornment}
              </Typography>
              <Typography
                color="secondary"
                fontSize={21}
                gutterBottom
                marginBottom="10px"
                marginLeft={['CAE', 'Interés mensual'].includes(item.label) ? '10px' : '0'}
              >
                {item.value}
              </Typography>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '4.5%' }}>
          <div>
            <Typography
              color="text.primary"
              variant="body2"
            >
              Monto total aproximado del
              {' '}
              {productName(productType)}
            </Typography>
            <Typography
              fontSize={25}
              gutterBottom
              style={{ color: theme.palette.secondary.main }}
            >
              {parseAmountWithCurrency(
                currency,
                loanData.amount + loanData.originCost + loanData.taxes + loanData.insuranceCost,
              )}
            </Typography>
          </div>
          <Typography
            color="text.primary"
            fontWeight="bold"
          >
            Costo total aproximado del
            {' '}
            {productName(productType)}
          </Typography>
          <Typography
            fontWeight="bold"
            fontSize={30}
            gutterBottom
            style={{ color: theme.palette.secondary.main }}
          >
            {parseAmountWithCurrency(
              currency,
              loanData.totalCost,
            )}
          </Typography>
        </div>
      </>
    </>
  );
}
